import type { MutableSnapshot } from "recoil"

import type {
  SolarDesignerAvailableDto,
  SolarDesignerImageryDto,
  SolarDesignerInstallAreaProductionDto,
} from "~/api/dto/solar-proposal-designer.dto"
import {
  findImageState,
  imageIndex,
  mapImageToKey,
} from "~/containers/LegacyDesigner/state/imageElements"
import {
  designSchema,
  newEquipmentSchema,
} from "~/containers/Proposal/state/designs/design-model"
import type { Design } from "~/containers/Proposal/state/designs/design-model"
import {
  designRemoteState,
  estimatesRemoteState,
} from "~/containers/Proposal/state/designs/design-state"
import { designInitialLoadCompleteState } from "~/containers/Proposal/state/designs/designInitialLoadComplete"
import { loadDesignIntoElements } from "~/containers/Proposal/state/designs/load-design-into-elements"
import { allEquipmentForSiteState } from "~/containers/Proposal/state/equipment/equipment-state"
import {
  DEFAULT_PROPOSAL,
  proposalLocalState,
  proposalRemoteState,
} from "~/containers/Proposal/state/proposal/proposal-state"
import {
  DEFAULT_SITE,
  siteLocalState,
  siteRemoteState,
} from "~/containers/Proposal/state/site/site-state"
import { DEFAULT_USER } from "~/containers/Proposal/state/user/constants"
import { currentUserState } from "~/containers/Proposal/state/user/user-state"
import type {
  EstimatesForDesignQuery,
  Imagery,
  ImagerySource,
  Proposal,
  StateCode,
  SystemType,
  User,
} from "~/graphql/client"

const DEFAULT_SIDE = 1024

/** The design a REST response carries, in the designer's own model. */
export const parseDesignerDesign = (design: unknown): Design =>
  designSchema.parse(design)

const toImagery = (image: SolarDesignerImageryDto): Imagery => ({
  __typename: `Imagery`,
  id: image.id,
  createdAt: image.createdAt,
  updatedAt: image.updatedAt,
  filename: image.filename ?? ``,
  imageDate: image.imageDate,
  source: image.source as ImagerySource,
  height: image.height,
  width: image.width,
  zoomLevel: image.zoomLevel,
  positionedX: image.positionedX,
  positionedY: image.positionedY,
  positionedZoom: image.positionedZoom,
  design: null,
  site: null,
  siteId: image.siteId,
})

/**
 * Per-area production as the Summary panel's estimates selector reads them:
 * only `installAreaId` and `acAnnual` are shown, and only `Estimate` rows count.
 */
export const toDesignerEstimates = (
  production: readonly SolarDesignerInstallAreaProductionDto[],
): EstimatesForDesignQuery[`estimatesForDesign`] =>
  production.map(
    ({ installAreaId, acAnnualKwh }) =>
      ({
        __typename: `Estimate`,
        id: installAreaId,
        installAreaId,
        acAnnual: acAnnualKwh,
        acMonthly: [],
        acHourly: [],
        errors: [],
        designId: null,
        solarProfileId: null,
        solarProfile: null,
        inputs: null,
        createdAt: null,
        updatedAt: null,
      }) as unknown as EstimatesForDesignQuery[`estimatesForDesign`][number],
  )

/**
 * The designer's scoped RecoilRoot, filled from the V2 REST bundle as
 * `PublicPresentation.initializePresentation` fills its own: the design and its
 * elements, the site and its images, the module the panels scale from, a
 * minimal proposal, the per-area production and the current user. The user is
 * set before the first render so phase gating and the designer's permission
 * check read the real permissions.
 */
export const hydrateDesignerState =
  (bundle: SolarDesignerAvailableDto, currentUser: Partial<User>) =>
  ({ set }: MutableSnapshot): void => {
    const design = parseDesignerDesign(bundle.design)
    set(designRemoteState, design)
    loadDesignIntoElements(set, design)
    set(designInitialLoadCompleteState, true)

    const imagery = bundle.site.imagery.map(toImagery)
    const site: typeof DEFAULT_SITE = {
      ...DEFAULT_SITE,
      id: bundle.site.id,
      address: bundle.site.address,
      street: bundle.site.street,
      locality: bundle.site.locality,
      postalCode: bundle.site.postalCode,
      state: bundle.site.state as StateCode,
      latitude: bundle.site.latitude,
      longitude: bundle.site.longitude,
      streetViewLatitude: bundle.site.streetViewLatitude ?? bundle.site.latitude,
      streetViewLongitude:
        bundle.site.streetViewLongitude ?? bundle.site.longitude,
      imagery: imagery as typeof DEFAULT_SITE.imagery,
    }
    set(siteLocalState, site)
    set(siteRemoteState, site)
    set(
      imageIndex,
      imagery.map((image) => mapImageToKey(image)),
    )
    for (const image of imagery) {
      const width = image.width || DEFAULT_SIDE
      const height = image.height || DEFAULT_SIDE
      set(findImageState(mapImageToKey(image)), {
        source: image.source,
        filename: image.filename,
        imageDate: image.imageDate,
        x: (DEFAULT_SIDE - width) / 2,
        y: (DEFAULT_SIDE - height) / 2,
        w: width,
        h: height,
        zoomLevel: image.zoomLevel ?? 20,
        isLocked: true,
      })
    }

    set(
      allEquipmentForSiteState,
      bundle.equipment.map((item) => newEquipmentSchema.parse(item)),
    )

    const proposal: Proposal = {
      ...DEFAULT_PROPOSAL,
      id: bundle.proposal.id,
      sellerId: bundle.proposal.sellerId,
      mentorId: bundle.proposal.mentorId,
      systemType: bundle.proposal.systemType as SystemType | null,
      annualEnergyUse: bundle.proposal.annualEnergyUseKwh,
      homeowner: {
        ...DEFAULT_PROPOSAL.homeowner,
        firstName: bundle.proposal.homeowner.firstName,
        lastName: bundle.proposal.homeowner.lastName,
      },
      site: { ...DEFAULT_PROPOSAL.site, ...site, imagery: [] },
    }
    set(proposalLocalState, proposal)
    set(proposalRemoteState, proposal)
    set(estimatesRemoteState, toDesignerEstimates(bundle.installAreaProduction))
    set(currentUserState, { ...DEFAULT_USER, ...currentUser } as User)
  }
